import { useMemo, useState } from 'react';
import {
  useCustomEventAdd,
  useCustomEventDelete,
  useCustomEventDrag,
  useCustomEventEdit,
  useCustomEventResize,
  useCustomEventSplit,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { AllocationModalParams } from '../../manage-allocation';
import { ExtendedManagerProps } from '../types';
import {
  useAllocationAddHandler,
  useAllocationDeleteHandler,
  useAllocationDragHandler,
  useAllocationEditHandler,
  useAllocationEventCancelHandler,
  useAllocationEventConfirmHandler,
  useAllocationResizeHandler,
  useAllocationSplitHandler,
} from './allocation-event-handlers';

interface ReturnProps {
  allocationModalParams?: AllocationModalParams;
  onEventActionConfirm: (
    allocationModalParams: AllocationModalParams,
  ) => Promise<void>;
  onEventActionCancel: () => void;
  onEventDelete: () => Promise<void>;
  onEnableSplitMode: (eventId?: number) => void;
}

export const useAllocationManager = (
  props: ExtendedManagerProps,
): ReturnProps => {
  const { schedulerRef } = props;
  const [allocationModalParams, setAllocationModalParams] =
    useState<AllocationModalParams>();
  const [currentEvent, setCurrentEvent] = useState<AllocationEventModel>();

  const addAndEditEventProps = useMemo(
    () => ({
      ...props,
      setAllocationModalParams,
      setCurrentEvent,
    }),
    [props],
  );

  const eventConfirmAndCancelProps = useMemo(
    () => ({
      ...props,
      currentEvent,
      setCurrentEvent,
      allocationModalParams,
      setAllocationModalParams,
    }),
    [allocationModalParams, currentEvent, props],
  );

  useCustomEventAdd(
    schedulerRef,
    useAllocationAddHandler(addAndEditEventProps),
  );
  useCustomEventEdit(
    schedulerRef,
    useAllocationEditHandler(addAndEditEventProps),
  );
  const deleteAllocationHandler = useAllocationDeleteHandler(props);

  useCustomEventDelete(schedulerRef, deleteAllocationHandler);
  useCustomEventSplit(schedulerRef, useAllocationSplitHandler(props));
  useCustomEventResize(schedulerRef, useAllocationResizeHandler(props));
  useCustomEventDrag(schedulerRef, useAllocationDragHandler(props));

  return {
    allocationModalParams,
    onEventActionConfirm: useAllocationEventConfirmHandler(
      eventConfirmAndCancelProps,
    ),
    onEventActionCancel: useAllocationEventCancelHandler(
      eventConfirmAndCancelProps,
    ),
    onEventDelete: () => {
      return deleteAllocationHandler({ event: currentEvent });
    },
    onEnableSplitMode: (eventId?: number) => {
      if (eventId != null) {
        schedulerRef.current.instance.eventStore.getById(
          eventId,
        ).splitModeActive = true;
      }
    },
  };
};
